import { Fragment, useState } from "react";
import { Pencil, Trash2, Receipt, MessageCircle, ClipboardCheck } from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { StatusBadge } from "./Common";
import { ConfirmButton } from "./ConfirmButton";
import { dateLabel, formatIDR } from "../lib/api";

function PayType({ t }) {
  if (t.status === "order" && !t.collect) return null;
  if (!t.collect) return <span className="block text-[10px] font-semibold text-sky-700" data-testid={`paytype-${t.id}`}>Voucher</span>;
  return (
    <span className="block text-[10px] font-semibold text-orange-700" data-testid={`paytype-${t.id}`}>
      Collect {formatIDR(t.collect)}{t.status !== "order" && <> · Bayar {formatIDR(t.payout)}</>}
    </span>
  );
}

function OrderInfo({ t }) {
  const [open, setOpen] = useState(false);
  const bits = [t.status === "order" && t.pax ? `${t.pax} pax` : null, t.trip_name, t.booking_platform, t.pickup_time && `Jemput ${t.pickup_time}`, t.pickup_location, t.guest_name && `Tamu: ${t.guest_name}`, t.guest_phone].filter(Boolean);
  if (!bits.length && !t.driver_note) return null;
  return (
    <div className="text-[11px] font-normal text-slate-500 mt-0.5 max-w-xs" data-testid={`order-info-${t.id}`}>
      {bits.join(" · ")}
      {t.driver_note && (
        <>
          <p className={`italic whitespace-pre-line ${open ? "" : "line-clamp-2"}`}>Catatan: {t.driver_note}</p>
          <button type="button" onClick={() => setOpen(!open)} className="font-semibold not-italic text-emerald-700 hover:underline" data-testid={`order-note-toggle-${t.id}`}>
            {open ? "Tutup" : "Lihat detail"}
          </button>
        </>
      )}
    </div>
  );
}

function OrderActions({ t, onEdit, onDelete, onSendWa }) {
  if (!onSendWa)
    return (
      <button onClick={() => onEdit(t)} className="inline-flex items-center gap-1.5 h-11 sm:h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700" data-testid={`complete-order-${t.id}`}>
        <ClipboardCheck className="h-4 w-4" /> Lengkapi
      </button>
    );
  return (
    <div className="flex gap-1">
      <button onClick={() => onSendWa(t)} className="h-11 w-11 sm:h-9 sm:w-9 grid place-items-center rounded-lg text-[#128C7E] hover:bg-emerald-50" data-testid={`send-wa-${t.id}`} aria-label="Kirim WA">
        <MessageCircle className="h-4 w-4" />
      </button>
      <button onClick={() => onEdit(t)} className="h-11 w-11 sm:h-9 sm:w-9 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100" data-testid={`edit-trip-${t.id}`} aria-label="Edit">
        <Pencil className="h-4 w-4" />
      </button>
      <ConfirmButton title="Hapus order?" description="Order ini akan dihapus permanen." onConfirm={() => onDelete(t)} testId={`delete-trip-${t.id}`} confirmLabel="Hapus">
        <button className="h-11 w-11 sm:h-9 sm:w-9 grid place-items-center rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid={`delete-trip-${t.id}`} aria-label="Hapus"><Trash2 className="h-4 w-4" /></button>
      </ConfirmButton>
    </div>
  );
}

function Actions({ t, onEdit, onDelete, onViewProof, onSendWa }) {
  if (t.status === "order") return <OrderActions t={t} onEdit={onEdit} onDelete={onDelete} onSendWa={onSendWa} />;
  if (t.status === "paid")
    return (
      <button onClick={() => onViewProof(t.payment_id)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:underline min-h-[36px]" data-testid={`view-proof-${t.id}`}>
        <Receipt className="h-4 w-4" /> Bukti Transfer
      </button>
    );
  return (
    <div className="flex gap-1">
      <button onClick={() => onEdit(t)} className="h-11 w-11 sm:h-9 sm:w-9 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100" data-testid={`edit-trip-${t.id}`} aria-label="Edit">
        <Pencil className="h-4 w-4" />
      </button>
      <ConfirmButton title="Hapus report trip?" description="Data trip ini akan dihapus permanen." onConfirm={() => onDelete(t)} testId={`delete-trip-${t.id}`} confirmLabel="Hapus">
        <button className="h-11 w-11 sm:h-9 sm:w-9 grid place-items-center rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid={`delete-trip-${t.id}`} aria-label="Hapus">
          <Trash2 className="h-4 w-4" />
        </button>
      </ConfirmButton>
    </div>
  );
}

function ExtrasText({ extras }) {
  if (!extras.length) return <span className="text-slate-300">—</span>;
  return (
    <div className="space-y-0.5">
      {extras.map((e, i) => (
        <p key={i} className="text-xs text-slate-600">{e.description}: <span className="font-num">{formatIDR(e.amount)}</span></p>
      ))}
    </div>
  );
}

function SelectBox({ t, selectable, selected, onToggle }) {
  if (!selectable) return null;
  return t.status === "pending" ? (
    <Checkbox checked={selected.includes(t.id)} onCheckedChange={() => onToggle(t)} data-testid={`select-trip-${t.id}`} />
  ) : <span className="block w-4" />;
}

export function TripList({ trips, showDriver, selectable, selected = [], onToggle, onEdit, onDelete, onViewProof, onSendWa }) {
  return (
    <>
      <div className="hidden md:block rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="trip-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              {selectable && <th className="w-10 px-4" />}
              <th className="text-left px-4 py-3">Tanggal</th>
              {showDriver && <th className="text-left px-4 py-3">Driver</th>}
              <th className="text-left px-4 py-3">Tour</th>
              <th className="text-right px-4 py-3">Harga/Trip</th>
              <th className="text-right px-4 py-3">Tiket</th>
              <th className="text-right px-4 py-3">Aqua</th>
              <th className="text-left px-4 py-3">Extra</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">Susuk</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {trips.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors" data-testid={`trip-row-${t.id}`}>
                {selectable && <td className="px-4"><SelectBox t={t} selectable selected={selected} onToggle={onToggle} /></td>}
                <td className="px-4 py-3 whitespace-nowrap">{dateLabel(t.trip_date)}</td>
                {showDriver && <td className="px-4 py-3 font-medium">{t.driver_name}</td>}
                <td className="px-4 py-3 font-medium text-slate-900">{t.tour_name}<OrderInfo t={t} /></td>
                <td className="px-4 py-3 text-right font-num">{formatIDR(t.price_per_trip)}</td>
                <td className="px-4 py-3 text-right font-num whitespace-nowrap">
                  {t.pax ? <>{formatIDR(t.ticket_total)}<p className="text-[11px] text-slate-400">{t.pax} org × {formatIDR(t.ticket_price)}</p></> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-3 text-right font-num whitespace-nowrap" data-testid={`trip-aqua-${t.id}`}>
                  {t.aqua_total ? <>{formatIDR(t.aqua_total)}<p className="text-[11px] text-slate-400">{t.aqua_qty} × {formatIDR(t.aqua_price)}</p></> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-3"><ExtrasText extras={t.extras} /></td>
                <td className="px-4 py-3 text-right font-num font-bold text-emerald-700 whitespace-nowrap" data-testid={`trip-total-${t.id}`}>{formatIDR(t.grand_total)}<PayType t={t} /></td>
                <td className="px-4 py-3 text-right font-num whitespace-nowrap text-amber-700" data-testid={`trip-susuk-${t.id}`}>{t.susuk ? formatIDR(t.susuk) : <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3"><StatusBadge status={t.status} testId={`trip-status-${t.id}`} /></td>
                <td className="px-4 py-3"><Actions t={t} onEdit={onEdit} onDelete={onDelete} onViewProof={onViewProof} onSendWa={onSendWa} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3" data-testid="trip-cards">
        {trips.map((t) => (
          <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`trip-card-${t.id}`}>
            <div className="flex items-start gap-3">
              <SelectBox t={t} selectable={selectable} selected={selected} onToggle={onToggle} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500">{dateLabel(t.trip_date)}{showDriver && ` · ${t.driver_name}`}</p>
                    <p className="font-heading font-semibold text-slate-900 truncate">{t.tour_name}</p>
                    <OrderInfo t={t} />
                  </div>
                  <StatusBadge status={t.status} />
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-3 text-xs">
                  <span className="text-slate-500">Harga/Trip</span><span className="text-right font-num">{formatIDR(t.price_per_trip)}</span>
                  <span className="text-slate-500">Tiket ({t.pax} org)</span><span className="text-right font-num">{formatIDR(t.ticket_total)}</span>
                  {t.aqua_total > 0 && <><span className="text-slate-500">Aqua ({t.aqua_qty} pax)</span><span className="text-right font-num">{formatIDR(t.aqua_total)}</span></>}
                  {t.extras.map((e, i) => (
                    <Fragment key={i}><span className="text-slate-500 truncate">+ {e.description}</span><span className="text-right font-num">{formatIDR(e.amount)}</span></Fragment>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <span className="font-num font-bold text-emerald-700">{formatIDR(t.grand_total)}<PayType t={t} />{t.susuk > 0 && <span className="block text-[11px] font-medium text-amber-700">Susuk {formatIDR(t.susuk)}</span>}</span>
                  <Actions t={t} onEdit={onEdit} onDelete={onDelete} onViewProof={onViewProof} onSendWa={onSendWa} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
